import {
    instance as axios,
    setJwtToken,
    setMultiPartHeader,
  } from "../../config/axiosInstance";
  
/**
 * Count Tracking Service for Frontend
 * Handles all count tracking operations
 */
class CountTrackingService {
  
  /**
   * Get unread counts for all menu types
   * @returns {Promise<Object>} Unread counts by type
   */
  static async getUnreadCounts() {
    try {
      const endPoint = `/admin/count-tracking/unread-counts`;
      let response = await axios.get(endPoint, setJwtToken());

      return response.data.data;
    } catch (error) {
      console.error('Error fetching unread counts:', error);
      return {
        cancelled_consultation: 0,
        waitlist_management: 0,
        missed_call: 0,
        progress_call: 0,
        completed_call: 0,
        support_management: 0,
        booked_session: 0
      };  
    }
  }

  /**
   * Get dashboard statistics
   * @returns {Promise<Object>} Dashboard stats
   */
  static async getDashboardStats() {
    try {
      const endPoint = `/admin/count-tracking/dashboard`;
      return await axios.get(endPoint, setJwtToken());
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        unreadCounts: {},
        totalEntries: 0,
        recentActions: [],
        activitySummary: []
      };
    }
  }

  /**
   * Mark entries as read
   * @param {Array} entryIds - Array of entry IDs to mark as read
   * @param {string} type - Type to mark as read
   * @returns {Promise<Object>} Result of the operation
   */
  static async markAsRead(entryIds = null, type = null) {
    try {
      const endPoint = `/admin/count-tracking/mark-read`;
      return await axios.post(endPoint, {entryIds, type}, setJwtToken())

    
    } catch (error) {
      console.error('Error marking entries as read:', error);
      throw error;
    }
  }

  /**
   * Get entries by type with pagination
   * @param {string} type - Entry type
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Paginated entries
   */
  static async getEntriesByType(type, options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.limit) params.append('limit', options.limit);
      if (options.status) params.append('status', options.status);
      if (options.priority) params.append('priority', options.priority);
      if (options.isRead !== null) params.append('isRead', options.isRead);
      if (options.sortBy) params.append('sortBy', options.sortBy);
      if (options.sortOrder) params.append('sortOrder', options.sortOrder);

      const endPoint = `/admin/count-tracking/entries/${type}?${params}`;
      return await axios.get(endPoint, setJwtToken());
    } catch (error) {
      console.error('Error fetching entries by type:', error);
      throw error;
    }
  }

  /**
   * Log user action
   * @param {Object} actionData - Action data
   * @returns {Promise<Object>} Result of the operation
   */
  static async logAction(actionData) {
    try {
      const endPoint = `/admin/count-tracking/log-action`;
      return await axios.post(endPoint, actionData, setJwtToken());
    } catch (error) {
      console.error('Error logging action:', error);
      throw error;
    }
  }

  /**
   * Get admin activity summary
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Array>} Activity summary
   */
  static async getActivitySummary(startDate = null, endDate = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());

      const endPoint = `/admin/count-tracking/activity/summary?${params}`;
      return await axios.get(endPoint, setJwtToken());
    } catch (error) {
      console.error('Error fetching activity summary:', error);
      return [];
    }
  }

  /**
   * Get recent admin actions
   * @param {number} limit - Number of actions to retrieve
   * @returns {Promise<Array>} Recent actions
   */
  static async getRecentActions(limit = 50) {
    try {
      const endPoint = `/admin/count-tracking/activity/recent?limit=${limit}`;
      return await axios.get(endPoint, setJwtToken());
    } catch (error) {
      console.error('Error fetching recent actions:', error);
      return [];
    }
  }

  /**
   * Update entry status
   * @param {string} entryId - Entry ID
   * @param {string} status - New status
   * @param {Object} metadata - Additional metadata
   * @returns {Promise<Object>} Updated entry
   */
  static async updateEntryStatus(entryId, status, metadata = {}) {
    try {
      const endPoint = `/admin/count-tracking/entry/${entryId}/status`;
      return await axios.put(endPoint, {
        status,
        metadata
      });
    } catch (error) {
      console.error('Error updating entry status:', error);
      throw error;
    }
  }

  /**
   * Update entry priority
   * @param {string} entryId - Entry ID
   * @param {string} priority - New priority
   * @returns {Promise<Object>} Updated entry
   */
  static async updateEntryPriority(entryId, priority) {
    try {
      const endPoint = `/admin/count-tracking/entry/${entryId}/priority`;
      return await axios.put(endPoint, {
        priority
      });
    } catch (error) {
      console.error('Error updating entry priority:', error);
      throw error;
    }
  }

  /**
   * Assign entry to admin
   * @param {string} entryId - Entry ID
   * @param {string} assignedTo - Admin ID to assign to
   * @returns {Promise<Object>} Updated entry
   */
  static async assignEntry(entryId, assignedTo) {
    try {
        const endPoint = `/admin/count-tracking/entry/${entryId}/assign`;
      return await axios.put(endPoint, {
        assignedTo
      });
    } catch (error) {
      console.error('Error assigning entry:', error);
      throw error;
    }
  }

  /**
   * Get overview statistics
   * @param {Date} startDate - Start date
   * @param {Date} endDate - End date
   * @returns {Promise<Object>} Overview statistics
   */
  static async getOverviewStats(startDate = null, endDate = null) {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());

      const endPoint = `/admin/count-tracking/stats/overview?${params}`;
      return await axios.get(endPoint, setJwtToken());
    } catch (error) {
      console.error('Error fetching overview stats:', error);
      throw error;
    }
  }

  /**
   * Track menu click
   * @param {string} menuItem - Menu item name
   * @param {string} page - Current page
   * @returns {Promise<void>}
   */
  static async trackMenuClick(menuItem, page = window.location.pathname) {
    try {
      await this.logAction({
        action: 'click_menu',
        metadata: {
          menuItem,
          page
        },
        context: {
          page,
          section: page.split('/')[2]
        }
      });
    } catch (error) {
      console.error('Error tracking menu click:', error);
    }
  }

  /**
   * Track item view
   * @param {string} itemId - Item ID
   * @param {string} itemType - Item type
   * @param {string} page - Current page
   * @returns {Promise<void>}
   */
  static async trackItemView(itemId, itemType, page = window.location.pathname) {
    try {
      await this.logAction({
        action: 'view_item',
        metadata: {
          itemId,
          itemType
        },
        context: {
          page,
          section: page.split('/')[2]
        }
      });
    } catch (error) {
      console.error('Error tracking item view:', error);
    }
  }

  /**
   * Track filter application
   * @param {Object} filters - Applied filters
   * @param {string} page - Current page
   * @returns {Promise<void>}
   */
  static async trackFilterApplied(filters, page = window.location.pathname) {
    try {
      await this.logAction({
        action: 'filter_applied',
        metadata: {
          filters,
          filterCount: Object.keys(filters).length
        },
        context: {
          page
        }
      });
    } catch (error) {
      console.error('Error tracking filter application:', error);
    }
  }

  /**
   * Track search operation
   * @param {string} searchQuery - Search query
   * @param {string} searchType - Search type
   * @param {number} resultCount - Number of results
   * @param {string} page - Current page
   * @returns {Promise<void>}
   */
  static async trackSearch(searchQuery, searchType, resultCount, page = window.location.pathname) {
    try {
      await this.logAction({
        action: 'search_performed',
        metadata: {
          searchQuery,
          searchType,
          resultCount
        },
        context: {
          page
        }
      });
    } catch (error) {
      console.error('Error tracking search:', error);
    }
  }
}

export default CountTrackingService;
